/**
 * Modern Typing Speed Classifications Section (CPM-based)
 * Uses your color variables and animal images. Responsive and visually rich.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/TypingSpeedClassifications.css';

const rankImg = (file) => {
  const path = `/images/${file}`;
  try {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return path;
  } catch (e) {}
  return `https://typeclicktests.com${path}`;
};
const snailImg = rankImg('snail-typing-speed-rank.jpg');
const turtleImg = rankImg('turtle-typing-speed-rank.jpg');
const rabbitImg = rankImg('rabbit-typing-speed-rank.jpg');
const wolfImg = rankImg('wolf-typing-speed-rank.jpg');
const cheetahImg = rankImg('cheetah-typing-speed-rank.jpg');
const falconImg = rankImg('falcon-typing-speed-rank.jpg');

const typingLevels = [
  {
    name: 'Snail',
    value: '0-100 CPM',
    level: 'Beginner',
    description: 'The methodical typist who prioritizes accuracy over speed. Every keystroke is deliberate and carefully placed, creating a steady rhythm of progress.',
    img: snailImg,
    slug: 'snail',
    percent: 15
  },
  {
    name: 'Turtle',
    value: '101-200 CPM',
    level: 'Novice',
    description: 'Slow but steady wins the race. Turtle typists maintain consistent accuracy while gradually building speed, creating reliable and precise text input.',
    img: turtleImg,
    slug: 'turtle',
    percent: 30
  },
  {
    name: 'Rabbit',
    value: '201-300 CPM',
    level: 'Intermediate',
    description: 'Quick bursts of speed with occasional pauses. Rabbit typists demonstrate good keyboard knowledge but may need to work on consistency to reach higher levels.',
    img: rabbitImg,
    slug: 'rabbit',
    percent: 50
  },
  {
    name: 'Wolf',
    value: '301-400 CPM',
    level: 'Advanced',
    description: 'Precise and swift, wolf typists have mastered the keyboard with excellent finger positioning and minimal errors, allowing them to soar through text.',
    img: wolfImg,
    slug: 'wolf',
    percent: 70
  },
  {
    name: 'Cheetah',
    value: '401-500 CPM',
    level: 'Expert',
    description: 'Lightning-fast with remarkable accuracy, cheetah typists represent the elite of keyboard warriors, able to maintain incredible speeds with minimal effort.',
    img: cheetahImg,
    slug: 'cheetah',
    percent: 90
  },
  {
    name: 'Falcon',
    value: '501+ CPM',
    level: 'Legendary',
    description: 'Beyond natural limits, falcon typists seem to defy physics with their supernatural typing speeds, achieving rates that blur the line between human and machine.',
    img: falconImg,
    slug: 'falcon',
    percent: 100
  }
];

const clickLevels = [
  {
    name: 'Snail',
    value: '0-3.99 CPS',
    level: 'Beginner',
    description: 'Slow and steady... but you can improve! Keep practicing to boost your click speed.',
    img: snailImg,
    slug: 'snail',
    percent: 15
  },
  {
    name: 'Turtle',
    value: '4-6.99 CPS',
    level: 'Novice',
    description: 'Steady and reliable clicking. You have good control, but there\'s room to go faster!',
    img: turtleImg,
    slug: 'turtle',
    percent: 30
  },
  {
    name: 'Rabbit',
    value: '7-9.99 CPS',
    level: 'Intermediate',
    description: 'Quick and nimble clicking. You\'re above average—keep pushing for the top ranks!',
    img: rabbitImg,
    slug: 'rabbit',
    percent: 50
  },
  {
    name: 'Wolf',
    value: '10-11.99 CPS',
    level: 'Advanced',
    description: 'Incredible reflexes and speed. You\'re a natural at click speed tests!',
    img: wolfImg,
    slug: 'wolf',
    percent: 70
  },
  {
    name: 'Cheetah',
    value: '12+ CPS',
    level: 'Expert',
    description: 'Lightning fast! Top 1% of clickers. Your reflexes are off the charts!',
    img: cheetahImg,
    slug: 'cheetah',
    percent: 90
  },
  {
    name: 'Falcon',
    value: '15+ CPS',
    level: 'Legendary',
    description: 'Falcon-level clickers are nearly superhuman. You\'ve reached the pinnacle of click speed!',
    img: falconImg,
    slug: 'falcon',
    percent: 100
  }
];

export default function TypingSpeedClassifications({ mode = 'typing' }) {
  const isClick = mode === 'click';
  const levels = isClick ? clickLevels : typingLevels;
  return (
    <section className="typing-speed-section">
      <h2 className="typing-speed-title">
        <span className="accent">{isClick ? 'CPS Click Speed' : 'Typing Speed'}</span> Ranks
        <div className="typing-speed-underline" />
      </h2>
      <p className="typing-speed-subtitle">
        {isClick ? (
          <>
            See how your CPS (Clicks Per Second) stacks up! From the steady snail to the lightning-fast cheetah, find your clicker persona.{' '}
            <Link to="/blog/what-is-a-good-cps/">What is a good CPS?</Link>
          </>
        ) : (
          <>
            See if you type like a Snail, Turtle, Rabbit, Wolf, or Cheetah. These ranks use CPM from the typing test — they are not a CPS click test.{' '}
            <Link to="/blog/cheetah-typing-test/">Cheetah typing test ranks explained</Link>
          </>
        )}
      </p>
      <div className="typing-speed-grid">
        {levels.map((level) => (
          <div className="typing-speed-card" key={level.name}>
            <figure className="typing-speed-figure">
              <div className={`typing-speed-img-wrap typing-speed-img-wrap--${level.slug}`}>
                <img
                  className="typing-speed-img"
                  src={level.img}
                  alt={`${level.name} ${isClick ? 'CPS click' : 'typing'} speed rank: ${level.level}, ${level.value}`}
                  width="512"
                  height="512"
                  decoding="async"
                  onError={(event) => {
                    event.currentTarget.style.opacity = '0';
                  }}
                />
                <div className="typing-speed-img-gradient" />
              </div>
              <figcaption className="typing-speed-caption">
                {level.name} rank — {level.level}, {level.value}
              </figcaption>
            </figure>
            <div className="typing-speed-card-content">
              <div className="typing-speed-card-header">
                <h3 className="typing-speed-card-name">{level.name}</h3>
                <span className="typing-speed-card-badge">{level.level}</span>
              </div>
              <div className="typing-speed-card-cpm">{level.value}</div>
              <div className="typing-speed-card-bar-bg">
                <div className="typing-speed-card-bar" style={{ width: `${level.percent}%` }} />
              </div>
              <div className="typing-speed-card-desc">{level.description}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="typing-speed-cta">
        <h3 className="cta-title">{isClick ? 'Ready to Test Your Click Speed?' : 'Ready to Test Your Typing Speed?'}</h3>
        <p className="cta-desc">
          {isClick
            ? 'Discover which animal represents your click speed. Take our click test now and see where you rank on the speed spectrum.'
            : 'Discover which animal represents your typing prowess. Take our advanced typing test now and see where you rank on the speed spectrum.'}
        </p>
        <Link className="cta-btn" to={isClick ? '/5s-cps-click-test/' : '/60s-cpm-typing-test/'}>
          {isClick ? 'Take the 5 second click test' : 'Take the 1 minute typing test'}
        </Link>
      </div>
    </section>
  );
} 